import { marketLocalDateKey } from "../markets/market-local-date.mjs";

export const BRIEF_JOURNEY_AUDIO_ARTICLE_LIMIT = 6;
export const ANDROID_CAROUSEL_NEWS_CANDIDATE_LIMIT = 25;
export const BRIEF_JOURNEY_AUDIO_MATCH_LIMIT = 3;
export const BRIEF_JOURNEY_AUDIO_TASK_LIMIT = 4;
export const NEWS_CATEGORY_AUDIO_ARTICLE_LIMIT = 60;

const LOCAL_NEWS_SOURCE_ID = "local-news-gemini";
const LOCAL_NEWS_WINDOW_DAYS = 5;
const STOCK_CHART_RANGE_CODE_RE = /(?<![A-Za-z0-9])1D(?![A-Za-z0-9])/gi;
const STOCK_CHART_CONTEXT_AFTER_RE =
  /^\s*(?:range|trend|average|return|change|performance|move|movement|momentum|window|view|chart|period|high|low)\b/i;
const STOCK_CHART_CONTEXT_BEFORE_RE = /(?:range|window|view|chart|period)\s*(?:[:=—-]\s*)?$/i;
const STOCK_PERCENT_BEFORE_RE = /[+\-−]?\d+(?:\.\d+)?\s*%\s*(?:[(]\s*)?$/;
const CURRENCY_BEFORE_RE = /[$€£₹¥]\s*$/;
const DOTTED_SYMBOL_AFTER_RE = /^\.[A-Za-z0-9]/;
const MARKET_DIRECTION_ARROW_PERCENT_RE = /([↑▲↗↓▼↘])\s*([+\-−]?\d+(?:\.\d+)?)\s*%/g;
const UP_MARKET_ARROWS = new Set(["↑", "▲", "↗"]);
const DOWN_MARKET_ARROWS = new Set(["↓", "▼", "↘"]);
const SOFT_LIFESTYLE_NEWS_TERMS = Object.freeze([
  "astrology",
  "horoscope",
  "rashifal",
  "rashiphal",
  "zodiac",
  "daily horoscope",
  "leo horoscope",
  "aries horoscope",
  "taurus horoscope",
  "gemini horoscope",
  "cancer horoscope",
  "virgo horoscope",
  "libra horoscope",
  "scorpio horoscope",
  "sagittarius horoscope",
  "capricorn horoscope",
  "aquarius horoscope",
  "pisces horoscope",
  "राशिफल",
  "आज का राशिफल",
  "ज्योतिष",
]);
const SOFT_LIFESTYLE_URL_SEGMENTS = Object.freeze(["/astrology/", "/horoscope/"]);

function clean(value) {
  return String(value || "").trim();
}

function cleanTaskText(value, maxChars) {
  return clean(value).replace(/\s+/g, " ").slice(0, maxChars).trim();
}

function canonicalTaskPriority(value) {
  if (typeof value === "number") {
    if (value >= 3) return "high";
    if (value === 2) return "medium";
    if (value === 1) return "low";
    return "none";
  }
  const priority = clean(value).toLowerCase();
  return ["high", "medium", "low"].includes(priority) ? priority : "none";
}

/**
 * Channel-neutral private task facts. The clients select the same bounded rows;
 * the backend owns localized status prose and the final speech identity.
 */
export function canonicalBriefNarrationTask(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const title = cleanTaskText(item.title || item.text || item.label, 4_000);
  if (!title) return null;
  const notes = cleanTaskText(item.notes || item.detail, 8_000);
  const dueDate = cleanTaskText(item.dueDate, 32);
  return {
    title,
    ...(notes ? { notes } : {}),
    priority: canonicalTaskPriority(item.priority),
    ...(dueDate ? { dueDate } : {}),
    dueToday: item.dueToday === true || item.isDueToday === true,
    overdue: item.overdue === true || item.isOverdue === true,
  };
}

export function canonicalBriefNarrationTasks(items) {
  return (Array.isArray(items) ? items : [])
    .map(canonicalBriefNarrationTask)
    .filter(Boolean)
    .slice(0, BRIEF_JOURNEY_AUDIO_TASK_LIMIT);
}

const MATCH_TEXT_FIELDS = Object.freeze([
  "matchId",
  "teams",
  "matchup",
  "homeTeam",
  "awayTeam",
  "competitionName",
  "competitionRound",
  "matchType",
  "phase",
  "status",
  "scoreLine",
  "resultLine",
  "resultOutcomeKind",
  "resultWinnerTeam",
  "resultMarginUnit",
  "detailLine",
  "kickoffUtc",
  "kickoffLabel",
  "venue",
]);
const MATCH_POSITIVE_INTEGER_FIELDS = Object.freeze(["resultMarginValue", "resultSecondaryRuns"]);
const MATCHUP_PARTICIPANT_SEPARATOR_RE = /\s+(?:v(?:s)?\.?|versus|at)\s+/i;
const SUPPORTED_NARRATION_CONTRACT_VERSION = 1;
const SUPPORTED_NARRATION_PHASES = new Set([
  "live",
  "break",
  "delayed",
  "scheduled",
  "completed",
  "cancelled",
  "postponed",
  "unknown",
]);
const TERMINAL_NARRATION_PHASES = new Set(["completed", "cancelled", "postponed"]);

/**
 * Canonical structured match shape shared by Home/Brief dashboard audio and
 * per-section Journey narration. These are facts only; the backend remains the
 * sole owner of localized prose, transitions, and final speech identity.
 */
export function canonicalBriefNarrationMatch(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const explicitHome = clean(item.homeTeam);
  const explicitAway = clean(item.awayTeam);
  const explicitPair =
    explicitHome && explicitAway && explicitHome.toLowerCase() !== explicitAway.toLowerCase();
  const parsedPair = [item.teams, item.matchup].some((value) => {
    const participants = clean(value)
      .split(MATCHUP_PARTICIPANT_SEPARATOR_RE)
      .map(clean)
      .filter(Boolean);
    return (
      participants.length === 2 && participants[0].toLowerCase() !== participants[1].toLowerCase()
    );
  });
  if (!explicitPair && !parsedPair) return null;
  const out = {};
  for (const field of MATCH_TEXT_FIELDS) {
    const value = clean(item[field]);
    if (value) out[field] = value;
  }
  for (const field of MATCH_POSITIVE_INTEGER_FIELDS) {
    const value = Number(item[field]);
    if (Number.isFinite(value) && value > 0) out[field] = Math.trunc(value);
  }
  return Object.keys(out).length ? out : null;
}

export function canonicalBriefNarrationMatches(items) {
  return (Array.isArray(items) ? items : [])
    .map(canonicalBriefNarrationMatch)
    .filter(Boolean)
    .slice(0, BRIEF_JOURNEY_AUDIO_MATCH_LIMIT);
}

function sportsFixtureNarrationMatch(fixture) {
  const home = clean(fixture?.homeTeam);
  const away = clean(fixture?.awayTeam);
  const teams = [home, away].filter(Boolean).join(" vs ");
  return canonicalBriefNarrationMatch({
    teams: teams || undefined,
    matchup: teams ? undefined : clean(fixture?.matchup) || undefined,
    homeTeam: home || undefined,
    awayTeam: away || undefined,
    competitionName: clean(fixture?.competitionName || fixture?.leagueName) || undefined,
    competitionRound: clean(fixture?.competitionRound || fixture?.leagueRound) || undefined,
    status: clean(fixture?.status || fixture?.statusText) || undefined,
    scoreLine: clean(fixture?.scoreLine) || undefined,
    resultLine: clean(fixture?.resultLine) || undefined,
    resultOutcomeKind: clean(fixture?.resultOutcomeKind) || undefined,
    resultWinnerTeam: clean(fixture?.resultWinnerTeam) || undefined,
    resultMarginValue: fixture?.resultMarginValue,
    resultMarginUnit: clean(fixture?.resultMarginUnit) || undefined,
    resultSecondaryRuns: fixture?.resultSecondaryRuns,
    detailLine: clean(fixture?.detailLine) || undefined,
    kickoffLabel: clean(fixture?.kickoffLabel || fixture?.kickoff) || undefined,
    venue: clean(fixture?.venue) || undefined,
  });
}

function completedSportsFixture(fixture) {
  if (clean(fixture?.resultLine) || clean(fixture?.resultOutcomeKind)) return true;
  return /(?:^|\b)(?:completed|ended|final|finished|ft)(?:\b|$)/i.test(
    clean(fixture?.status || fixture?.statusText),
  );
}

function iplNarrationMatches(payload, expectedCountry, nowEpochMs) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return [];
  if (Object.prototype.hasOwnProperty.call(payload, "narrationMatches")) {
    if (!canonicalNarrationProjectionIsTrusted(payload, expectedCountry)) {
      return [];
    }
    return canonicalNarrationMatchesForSnapshot(payload, payload.narrationMatches, nowEpochMs);
  }
  const payloadCountry = normalizedCountry(payload.narrationCountry || payload.country);
  if (
    expectedCountry &&
    (payloadCountry ? payloadCountry !== expectedCountry : expectedCountry !== "IN")
  ) {
    return [];
  }
  if (payload.ok === false || payload.noMatch === true) return [];
  if (payload.stale === true && payload.matchEnded !== true) return [];
  const teamNames = (Array.isArray(payload.teamInfo) ? payload.teamInfo : [])
    .map((team) => clean(team?.name || team?.shortname))
    .filter(Boolean);
  const scoreLine = (Array.isArray(payload.teams) ? payload.teams : [])
    .map((team) => [clean(team?.name), clean(team?.runsWickets)].filter(Boolean).join(" "))
    .filter(Boolean)
    .join(" · ");
  const match = canonicalBriefNarrationMatch({
    teams: teamNames.slice(0, 2).join(" vs ") || undefined,
    homeTeam: teamNames[0] || undefined,
    awayTeam: teamNames[1] || undefined,
    status: clean(payload.status || payload.message) || undefined,
    scoreLine: scoreLine || undefined,
    detailLine: clean(payload.iplScoreboard?.contextLine) || undefined,
    venue: clean(payload.venue) || undefined,
  });
  return match ? [match] : [];
}

function normalizedCountry(value) {
  const country = clean(value).toUpperCase();
  return /^[A-Z]{2}$/.test(country) ? country : "";
}

function normalizedSport(value) {
  const sport = clean(value)
    .toLowerCase()
    .replace(/^sports?_/, "");
  return sport === "soccer" ? "football" : sport;
}

function sportForBriefSection(sectionId) {
  const id = clean(sectionId).toLowerCase();
  if (id === "cricket") return "cricket";
  return id.startsWith("sport_") ? normalizedSport(id) : "";
}

function sportsSnapshotMatchesRequest(
  payload,
  expectedCountry,
  expectedSport,
  { requireCountry = false, requireSport = false, requireNarrationSport = false } = {},
) {
  const normalizedExpectedCountry = normalizedCountry(expectedCountry);
  const normalizedExpectedSport = normalizedSport(expectedSport);
  const rawCountries = [payload?.country, payload?.narrationCountry].map(clean).filter(Boolean);
  const countries = rawCountries.map(normalizedCountry);
  const rawSports = [payload?.sport, payload?.primarySport].map(clean).filter(Boolean);
  const sports = rawSports.map(normalizedSport);
  const rawNarrationSport = clean(payload?.narrationSport);
  const narrationSport = normalizedSport(payload?.narrationSport);
  return Boolean(
    countries.every(Boolean) &&
    sports.every(Boolean) &&
    (!requireCountry || countries.length > 0) &&
    (!normalizedExpectedCountry ||
      countries.length === 0 ||
      countries.every((country) => country === normalizedExpectedCountry)) &&
    (!requireSport || sports.length > 0) &&
    (!requireNarrationSport || narrationSport === normalizedExpectedSport) &&
    normalizedExpectedSport &&
    (sports.length === 0 || sports.every((sport) => sport === normalizedExpectedSport)) &&
    (!rawNarrationSport || narrationSport === normalizedExpectedSport),
  );
}

function nonNegativeFiniteNumber(value) {
  if (value == null || (typeof value === "string" && !value.trim())) return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function snapshotAgeSeconds(payload, nowEpochMs) {
  const ages = [];
  const reportedAge = nonNegativeFiniteNumber(payload?.dataFreshnessSeconds);
  if (reportedAge != null) ages.push(reportedAge);
  const fetchedAtEpochMs = Date.parse(clean(payload?.fetchedAt));
  if (Number.isFinite(fetchedAtEpochMs)) {
    ages.push(Math.max(0, nowEpochMs - fetchedAtEpochMs) / 1_000);
  }
  return ages.length ? Math.max(...ages) : null;
}

function canonicalSnapshotIsStale(payload, nowEpochMs) {
  const refreshPhase = clean(payload?.refreshPhase).toLowerCase();
  const refreshAfterSeconds = nonNegativeFiniteNumber(payload?.refreshAfterSeconds);
  const staleThresholdSeconds = Math.max(60, (refreshAfterSeconds || 0) * 3);
  const ageSeconds = snapshotAgeSeconds(payload, nowEpochMs);
  return Boolean(
    payload?.stale === true ||
    payload?.degraded === true ||
    payload?.refreshIncomplete === true ||
    ["backoff", "converging"].includes(refreshPhase) ||
    (ageSeconds != null && ageSeconds > staleThresholdSeconds),
  );
}

function canonicalNarrationMatchesForSnapshot(payload, items, nowEpochMs) {
  const matches = canonicalBriefNarrationMatches(items);
  if (!canonicalSnapshotIsStale(payload, nowEpochMs)) return matches;
  return matches.filter((match) => TERMINAL_NARRATION_PHASES.has(clean(match.phase).toLowerCase()));
}

function canonicalProjectionMatchIsValid(item) {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false;
  const home = clean(item.homeTeam);
  const away = clean(item.awayTeam);
  const phase = clean(item.phase).toLowerCase();
  return Boolean(
    home &&
    away &&
    home.toLowerCase() !== away.toLowerCase() &&
    SUPPORTED_NARRATION_PHASES.has(phase),
  );
}

function canonicalProjectionMetadataIsCoherent(payload) {
  const matches = payload.narrationMatches;
  const state = clean(payload.narrationProjectionState).toLowerCase();
  if (
    !Array.isArray(matches) ||
    matches.length > BRIEF_JOURNEY_AUDIO_MATCH_LIMIT ||
    !matches.every(canonicalProjectionMatchIsValid)
  ) {
    return false;
  }
  if (state === "ready") {
    return (
      payload.narrationMatchesAuthoritative === true &&
      payload.narrationNoFixture === false &&
      matches.length > 0
    );
  }
  if (state === "no_fixture") {
    return (
      payload.narrationMatchesAuthoritative === true &&
      payload.narrationNoFixture === true &&
      matches.length === 0
    );
  }
  if (state === "transitional") {
    return (
      payload.narrationMatchesAuthoritative != null &&
      payload.narrationNoFixture === false &&
      matches.length === 0
    );
  }
  return false;
}

function canonicalNarrationProjectionIsTrusted(
  payload,
  expectedCountry,
  requirePayloadCountry = false,
  expectedSport = "",
  requirePayloadSport = false,
  requireNarrationSport = false,
) {
  const narrationCountry = normalizedCountry(payload?.narrationCountry);
  const normalizedExpectedCountry = normalizedCountry(expectedCountry);
  return Boolean(
    payload?.narrationContractVersion === SUPPORTED_NARRATION_CONTRACT_VERSION &&
    narrationCountry &&
    normalizedExpectedCountry &&
    narrationCountry === normalizedExpectedCountry &&
    sportsSnapshotMatchesRequest(payload, normalizedExpectedCountry, expectedSport || "cricket", {
      requireCountry: requirePayloadCountry,
      requireSport: requirePayloadSport,
      requireNarrationSport,
    }) &&
    canonicalProjectionMetadataIsCoherent(payload),
  );
}

function canonicalSportsNarrationMatchesAvailable(sports, expectedCountry, expectedSport) {
  if (!sports || !Object.prototype.hasOwnProperty.call(sports, "narrationMatches")) {
    return false;
  }
  return canonicalNarrationProjectionIsTrusted(
    sports,
    expectedCountry,
    true,
    expectedSport,
    true,
    true,
  );
}

function legacySportsSnapshotIsConclusive(sports, nowEpochMs) {
  return Boolean(
    sports &&
    sports.ok !== false &&
    !canonicalSnapshotIsStale(sports, nowEpochMs) &&
    Array.isArray(sports.fixtures),
  );
}

/**
 * Convert the shared sports snapshot into the exact narration fact contract.
 * A market-bound canonical endpoint projection is authoritative even when
 * empty. Mixed-version fixture payloads may use the legacy IPL endpoint only
 * while the country-sports snapshot is absent, failed, stale-active, or still
 * converging. A fresh empty fixture snapshot remains an affirmative no-fixture
 * fence and must not be bypassed.
 */
export function briefNarrationMatchSelectionForSection({
  sectionId,
  country,
  sportsBySection = {},
  ipl,
  nowEpochMs,
} = {}) {
  const id = clean(sectionId).toLowerCase();
  if (id !== "cricket" && !id.startsWith("sport_")) {
    return { authoritative: false, matches: [] };
  }
  const requestedCountry = normalizedCountry(country);
  const requestedSport = sportForBriefSection(id);
  const effectiveNowEpochMs = nonNegativeFiniteNumber(nowEpochMs) ?? Date.now();
  const rawSports = sportsBySection?.[id];
  const canonicalProjectionPresent = Boolean(
    rawSports && Object.prototype.hasOwnProperty.call(rawSports, "narrationMatches"),
  );
  if (canonicalProjectionPresent) {
    return {
      authoritative: true,
      matches: canonicalSportsNarrationMatchesAvailable(rawSports, requestedCountry, requestedSport)
        ? canonicalNarrationMatchesForSnapshot(
            rawSports,
            rawSports.narrationMatches,
            effectiveNowEpochMs,
          )
        : [],
    };
  }
  const canonicalIplProjectionPresent = Boolean(
    id === "cricket" &&
      ipl &&
      Object.prototype.hasOwnProperty.call(ipl, "narrationMatches"),
  );
  if (canonicalIplProjectionPresent) {
    return {
      authoritative: true,
      matches: canonicalBriefNarrationMatches(
        iplNarrationMatches(ipl, requestedCountry, effectiveNowEpochMs),
      ),
    };
  }
  const sportsCountry = normalizedCountry(rawSports?.country);
  const sports = sportsSnapshotMatchesRequest(rawSports, requestedCountry, requestedSport)
    ? rawSports
    : null;
  const fixtures =
    sports?.ok !== false && Array.isArray(sports?.fixtures)
      ? sports.fixtures.filter(
          (fixture) =>
            !canonicalSnapshotIsStale(sports, effectiveNowEpochMs) ||
            completedSportsFixture(fixture),
        )
      : [];
  const matches = canonicalBriefNarrationMatches(fixtures.map(sportsFixtureNarrationMatch));
  if (
    matches.length ||
    id !== "cricket" ||
    legacySportsSnapshotIsConclusive(sports, effectiveNowEpochMs)
  ) {
    return { authoritative: false, matches };
  }
  return {
    authoritative: false,
    matches: canonicalBriefNarrationMatches(
      iplNarrationMatches(ipl, requestedCountry || sportsCountry, effectiveNowEpochMs),
    ),
  };
}

function briefNarrationSectionCanonicalProjectionState(input = {}) {
  const id = clean(input.sectionId).toLowerCase();
  if (id !== "cricket" && !id.startsWith("sport_")) return "absent";
  const requestedCountry = normalizedCountry(input.country);
  const requestedSport = sportForBriefSection(id);
  const sports = input.sportsBySection?.[id];
  if (sports && Object.prototype.hasOwnProperty.call(sports, "narrationMatches")) {
    return canonicalNarrationProjectionIsTrusted(
      sports,
      requestedCountry,
      true,
      requestedSport,
      true,
      true,
    )
      ? clean(sports.narrationProjectionState).toLowerCase()
      : "invalid";
  }
  if (
    requestedSport !== "cricket" ||
    !input.ipl ||
    !Object.prototype.hasOwnProperty.call(input.ipl, "narrationMatches")
  ) {
    return "absent";
  }
  return canonicalNarrationProjectionIsTrusted(
    input.ipl,
    requestedCountry,
    false,
    "cricket",
    false,
    true,
  )
    ? clean(input.ipl.narrationProjectionState).toLowerCase()
    : "invalid";
}

/** A coherent market-and-sport-bound projection explicitly resolved with no fixture. */
export function briefNarrationSectionHasAuthoritativeNoFixture(input = {}) {
  return briefNarrationSectionCanonicalProjectionState(input) === "no_fixture";
}

/** Canonical-contract presence with unsupported, malformed, or contradictory provenance. */
export function briefNarrationSectionHasInvalidCanonicalProjection(input = {}) {
  return briefNarrationSectionCanonicalProjectionState(input) === "invalid";
}

/**
 * Home and a fresh Brief run must suppress the same section when canonical data either proves
 * no fixture or fails validation. Transitional data remains visible and blocks readiness.
 */
export function briefNarrationSectionShouldBeHidden(input = {}) {
  const state = briefNarrationSectionCanonicalProjectionState(input);
  return state === "no_fixture" || state === "invalid";
}

export function briefNarrationSectionProjectionIsSettled(input = {}) {
  const state = briefNarrationSectionCanonicalProjectionState(input);
  if (state === "absent" || state === "no_fixture" || state === "invalid") return true;
  if (state === "transitional") return false;
  return briefNarrationMatchSelectionForSection(input).matches.length > 0;
}

export function briefNarrationMatchesForSection(input = {}) {
  return briefNarrationMatchSelectionForSection(input).matches;
}

function articlePublishedAtEpochMs(item) {
  const value = Number(item?.publishedAtEpochMs ?? item?.publishedAtMs);
  if (Number.isFinite(value) && value >= 0) return value;
  const parsed = Date.parse(String(item?.publishedAt || item?.pubDate || item?.date || ""));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function androidCarouselHeadlineKey(value) {
  return clean(value)
    .normalize("NFKC")
    .replace(/<[^>]*>/g, " ")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase("en-US");
}

function newsGuardText(item) {
  return [
    item?.title,
    item?.description,
    item?.categoryLabel,
    item?.sourceName,
    item?.publisherName,
    item?.link,
    item?.originalArticleUrl,
  ]
    .filter(Boolean)
    .join(" ")
    .normalize("NFKD")
    .toLocaleLowerCase("en-US")
    .replace(/\p{Mn}/gu, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function softLifestyleNewsArticle(item) {
  const text = newsGuardText(item);
  if (text && SOFT_LIFESTYLE_NEWS_TERMS.some((term) => text.includes(term))) return true;
  const rawUrl = clean(item?.originalArticleUrl || item?.link || item?.sourceUrl);
  if (!/^https?:\/\//i.test(rawUrl)) return false;
  try {
    const parsed = new URL(rawUrl);
    const surface = `${parsed.hostname} ${parsed.pathname}`.toLocaleLowerCase("en-US");
    return SOFT_LIFESTYLE_URL_SEGMENTS.some((segment) => surface.includes(segment));
  } catch {
    return false;
  }
}

function authoritativeNewsNarrationItem(item) {
  if (!item || typeof item !== "object") return null;
  if (!Object.prototype.hasOwnProperty.call(item, "narrationArticle")) return null;
  const narrationItem = item.narrationArticle;
  return narrationItem && typeof narrationItem === "object"
    ? narrationItem
    : null;
}

/** Mirrors Android's `(items + indiaItems).distinctBy { link ?: title }` boundary. */
export function mergeAndroidCarouselNewsItems(
  items,
  indiaItems,
  limit = ANDROID_CAROUSEL_NEWS_CANDIDATE_LIMIT,
) {
  const maxItems = Math.max(
    0,
    Math.min(ANDROID_CAROUSEL_NEWS_CANDIDATE_LIMIT, Math.trunc(Number(limit) || 0)),
  );
  if (maxItems === 0) return [];
  const seen = new Set();
  const merged = [];
  for (const item of [
    ...(Array.isArray(items) ? items : []),
    ...(Array.isArray(indiaItems) ? indiaItems : []),
  ]) {
    // Android maps blank links to null, then applies distinctBy { link ?: title }.
    // Canonical/original URLs are read targets, not repository identity.
    const identity = clean(item?.link) || clean(item?.title);
    if (!identity || seen.has(identity)) continue;
    seen.add(identity);
    merged.push(item);
    if (merged.length >= maxItems) break;
  }
  return merged;
}

/**
 * Select the exact six news facts used by Android and the daily default-audio
 * warmer. The API's editorial order remains authoritative except for the
 * shared lifestyle demotion; market-local dates reject future-edition rows.
 */
export function canonicalBriefNarrationNewsArticles(items, options = {}) {
  const country = clean(options?.country || "US").toUpperCase();
  const nowEpochMs = Number(options?.nowEpochMs);
  const marketToday = marketLocalDateKey(
    Number.isFinite(nowEpochMs) ? nowEpochMs : Date.now(),
    country,
  );
  const seenHeadlines = new Set();
  const deduped = [];
  for (const item of (Array.isArray(items) ? items : []).slice(
    0,
    ANDROID_CAROUSEL_NEWS_CANDIDATE_LIMIT,
  )) {
    // Field absence (old cache/replica) and explicit null both fail closed.
    // Narrating the display approximation would create a second paid TTS key.
    const narrationItem = authoritativeNewsNarrationItem(item);
    if (!narrationItem) continue;
    const canonical = canonicalAudioArticle(narrationItem);
    if (!canonical.title) continue;
    const headlineKey = androidCarouselHeadlineKey(canonical.title);
    if (headlineKey && seenHeadlines.has(headlineKey)) continue;
    if (headlineKey) seenHeadlines.add(headlineKey);
    deduped.push({ canonical, source: narrationItem });
  }
  const regular = deduped.filter((entry) => !softLifestyleNewsArticle(entry.source));
  const ordered = regular.length
    ? [
        ...regular,
        ...deduped.filter((entry) => softLifestyleNewsArticle(entry.source)),
      ]
    : deduped;
  return ordered
    .filter(({ canonical }) => {
      const publishedAtEpochMs = canonical.publishedAtEpochMs;
      return (
        publishedAtEpochMs === undefined ||
        marketLocalDateKey(publishedAtEpochMs, country) <= marketToday
      );
    })
    .slice(0, BRIEF_JOURNEY_AUDIO_ARTICLE_LIMIT)
    .map(({ canonical }) => canonical);
}

/** Android/prewarm canonical MarketExperience speech body. */
export function canonicalMarketExperienceNarrationBody(marketCard) {
  const headline = clean(marketCard?.headline);
  const bullets = (Array.isArray(marketCard?.journey?.bullets)
    ? marketCard.journey.bullets
    : []
  )
    .map(clean)
    .filter(Boolean)
    .slice(0, 3)
    .join(" · ");
  const summary = clean(marketCard?.summary);
  return [headline, bullets, summary && summary !== headline ? summary : ""]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Canonical article shape shared with Android's NewsCategoryAudioArticleDto.
 * URL/date-display fields stay client-side and never perturb narration identity.
 */
export function canonicalAudioArticle(item) {
  const sourceName = clean(item?.sourceName || item?.source);
  const publisherName = clean(item?.publisherName);
  const publishedAtEpochMs = articlePublishedAtEpochMs(item);
  return {
    title: clean(item?.title),
    description: clean(item?.description),
    ...(publishedAtEpochMs === undefined ? {} : { publishedAtEpochMs }),
    ...(sourceName ? { sourceName } : {}),
    ...(publisherName ? { publisherName } : {}),
  };
}

export function canonicalBriefJourneyAudioArticles(items) {
  return (Array.isArray(items) ? items : [])
    .map(canonicalAudioArticle)
    .filter((item) => item.title)
    .slice(0, BRIEF_JOURNEY_AUDIO_ARTICLE_LIMIT);
}

function articleEditorialKey(item) {
  const link = clean(item?.originalArticleUrl || item?.link || item?.url);
  return link || `t:${clean(item?.title)}`;
}

function shiftIsoDateKey(dateKey, days) {
  const date = new Date(`${dateKey}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * Mirrors Android NewsAudioArticleFilter.forAnchorScript: preserve the server's
 * editorial order, reject future calendar days, dedupe by link/title, cap at 60.
 * The local-news-gemini source additionally drops undated rows and keeps only
 * the last five local calendar days, matching forLocalNewsAnchorScript.
 */
export function filterNewsCategoryAudioArticles(items, options = {}) {
  const nowEpochMs =
    typeof options === "number" ? options : Number(options?.nowEpochMs) || Date.now();
  const isLocalNews =
    typeof options === "object" &&
    (options?.localNews === true ||
      clean(options?.sourceId).toLowerCase() === LOCAL_NEWS_SOURCE_ID);
  const country = typeof options === "object" ? options?.country : "US";
  const marketToday = marketLocalDateKey(nowEpochMs, country);
  const oldestLocalNewsDate = shiftIsoDateKey(
    marketToday,
    -(LOCAL_NEWS_WINDOW_DAYS - 1),
  );
  const seen = new Set();
  const filtered = [];
  for (const item of Array.isArray(items) ? items : []) {
    const narrationItem = authoritativeNewsNarrationItem(item);
    if (!narrationItem) continue;
    const canonical = canonicalAudioArticle(narrationItem);
    if (!canonical.title) continue;
    if (
      isLocalNews &&
      (canonical.publishedAtEpochMs === undefined ||
        marketLocalDateKey(canonical.publishedAtEpochMs, country) < oldestLocalNewsDate)
    ) {
      continue;
    }
    if (
      canonical.publishedAtEpochMs !== undefined &&
      marketLocalDateKey(canonical.publishedAtEpochMs, country) > marketToday
    ) {
      continue;
    }
    const key = articleEditorialKey(narrationItem);
    if (seen.has(key)) continue;
    seen.add(key);
    filtered.push(canonical);
    if (filtered.length >= NEWS_CATEGORY_AUDIO_ARTICLE_LIMIT) break;
  }
  return filtered;
}

function replaceMarketDirectionArrows(value) {
  return value.replace(MARKET_DIRECTION_ARROW_PERCENT_RE, (_match, arrow, signedAmountRaw) => {
    const signedAmount = String(signedAmountRaw).replace("−", "-");
    const magnitude = signedAmount.replace(/^[+-]/, "");
    const direction =
      DOWN_MARKET_ARROWS.has(arrow) || signedAmount.startsWith("-")
        ? "negative"
        : UP_MARKET_ARROWS.has(arrow) || signedAmount.startsWith("+")
          ? "positive"
          : "flat";
    return `${direction} ${magnitude}%`;
  });
}

function stripStockChartRangeCodes(value) {
  return value
    .replace(STOCK_CHART_RANGE_CODE_RE, (token, offset) => {
      const before = value.slice(0, offset);
      const after = value.slice(offset + token.length);
      if (CURRENCY_BEFORE_RE.test(before) || DOTTED_SYMBOL_AFTER_RE.test(after)) {
        return token;
      }
      const parenthesized = /\(\s*$/.test(before) && /^\s*\)/.test(after);
      const chartContext =
        STOCK_PERCENT_BEFORE_RE.test(before) ||
        STOCK_CHART_CONTEXT_BEFORE_RE.test(before) ||
        STOCK_CHART_CONTEXT_AFTER_RE.test(after);
      return parenthesized || chartContext ? "" : token;
    })
    .replace(/\s+([,.;:!?)])/g, "$1")
    .replace(/\(\s+/g, "(")
    .replace(/\(\s*\)/g, "")
    .replace(/\s+([,.;:!?)])/g, "$1")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Mirrors Android's Journey-only removal of stale UI chrome and chart tokens. */
export function sanitizeBriefJourneyNarrationSource(sectionId, itemType, source) {
  const section = clean(sectionId).toLowerCase();
  const item = clean(itemType).toLowerCase();
  const isWatchlist = section === "watchlist" || item === "watchlist";
  let value = clean(source);
  if (isWatchlist) {
    value = value
      .replace(/^\s*Here is your watchlist pulse(?:\s+for today)?\.\s*/i, "")
      .replace(/^\s*(\d{1,2}[.)]\s*)?Next\s+is\s+/i, "$1");
  } else if (item === "news" || section === "news" || section.startsWith("news_")) {
    value = value.replace(/^\s*Next\s+one[\s.:,-]+/i, "");
  }
  const speech = replaceMarketDirectionArrows(value);
  return isWatchlist ? stripStockChartRangeCodes(speech) : speech;
}
